import React from "react";
import moment from "moment";
import { Button, Link, makeStyles } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { DocumentList } from "@containers/DocumentList";
import { ThemeType } from "@theme";
import {
  BookingHospitalityFragment,
  BookingHospitalityGet,
  BookingScheduleSearch_Get,
  useRemoveTravelDocumentMutation,
  useToggleTravelConfirmationMutation
} from "@graphql";
import { useConfirmDialog, useNavManager, useSnackbars } from "@hooks";
import { OkCancelDialogOptions, UserAvatarChip } from "@containers";
import { PlainLink } from "@components";
import { store } from "@store";
import { BookingHospitalityDocumentSection } from "./components/BookingHospitalityDocumentSection";
import { TravelRequestLocation } from "./locations/TravelRequestLocation";
import { TravelConfirmationLocation } from "./locations/TravelConfirmationLocation";
import { TravelConfirmationViewLocation } from "./locations/TravelConfirmationViewLocation";
import { WeekYear } from "@bonliva-crm/common";

type Props = {
  bookingId: string;
  bookingGroupId: string;
  data: BookingHospitalityFragment;
  week: WeekYear;
};

export function BookingTravelSection(props: Props) {
  const { bookingId, data } = props;
  const classes = useStyles({});
  const { showSuccess, showError } = useSnackbars();
  const { confirm } = useConfirmDialog();
  const { navManager } = useNavManager();

  const [_toggleConfirmation] = useToggleTravelConfirmationMutation({
    refetchQueries: [
      BookingHospitalityGet.OperationName,
      BookingScheduleSearch_Get.OperationName
    ]
  });

  const [_removeDoc] = useRemoveTravelDocumentMutation({
    refetchQueries: [
      BookingHospitalityGet.OperationName,
      BookingScheduleSearch_Get.OperationName
    ]
  });

  const openRequestForm = () =>
    navManager.open(
      "travel_request_form",
      new TravelRequestLocation({
        bookingGroupId: props.bookingGroupId,
        bookingId: props.bookingId
      })
    );

  const openUploader = (files: File[]) => {
    store().navigation.open(
      "travel_doc_uploader",
      new TravelConfirmationLocation({
        bookingId: props.bookingId,
        files,
        week: props.week
      })
    );
  };

  const removeDoc = (fileId: string) =>
    confirm(new OkCancelDialogOptions()).then((res) => {
      if (res === "ok") {
        _removeDoc({ variables: { bookingId, fileId } })
          .then(() => showSuccess(locale.Deleted))
          .catch((err) => showError(`Cannot delete file\nReason: ${err.toString()}`));
      }
    });

  const toggleConfirmation = (id: string, isDeleted: boolean, weeks) => {
    if (!isDeleted) {
      confirm(
        new OkCancelDialogOptions(
          `${locale.AreYouSureRemoveConfirmation} ${weeks
            .map((x) => moment(x).format("GGGG.vW"))
            .join(", ")}`
        )
      ).then((res) => {
        if (res === "ok") {
          _toggleConfirmation({ variables: { bookingId, confirmationId: id } }).then(
            () => {
              showSuccess(locale.bookingSystemEvents.RemoveAccConfirmation);
            }
          );
        }
      });
    } else {
      _toggleConfirmation({ variables: { bookingId, confirmationId: id } }).then(() => {
        showSuccess(locale.bookingSystemEvents.RestoreAccConfirmation);
      });
    }
  };

  const openConfirmation = (confirmationId: string) => {
    navManager.open(
      "travel_confirmation",
      new TravelConfirmationViewLocation({ bookingId, confirmationId })
    );
  };

  let requestSentContent = null;

  if (data.travel.travelRequested) {
    requestSentContent = (
      <div>
        {data.travel.travelRequest.map((request) => {
          const date = moment.utc(request.createdOn).format("L");

          return (
            <div
              key={request._id}
              style={{ padding: "4px 0", display: "flex", alignItems: "center" }}
            >
              <UserAvatarChip size="small" userName={request.createdByName} />
              <PlainLink
                style={{ marginLeft: 4 }}
                onClick={() =>
                  store().navigation.open(
                    "travel_request_view",
                    new TravelRequestLocation({
                      bookingGroupId: props.bookingGroupId,
                      bookingId: props.bookingId,
                      requestId: request._id
                    })
                  )
                }
              >
                {`${locale.RequestedOn} ${date}`}
              </PlainLink>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <BookingHospitalityDocumentSection
      className={classes.item}
      title={locale.Travel}
      isOk={data.travel.travelIsOk}
      primaryButtonText="Beställ resa"
      dropToUploadText="Dra filer hit eller klicka här för att ladda upp resabekräftelse"
      switchText="Ingen resa behövs"
      onUpload={(files) => openUploader(files)}
      onMakeRequest={openRequestForm}
      requestSent={data.travel.travelRequested}
      requestSentContent={requestSentContent}
    >
      <div>
        {data.travel.confirmations.map((confirm) => (
          <React.Fragment key={confirm._id}>
            <div className={`${classes.docHeader} ${confirm.deleted ? "canceled" : ""}`}>
              <Link
                className={`${confirm.deleted ? "canceled" : ""}`}
                variant="subtitle2"
                color="secondary"
                display="inline"
                onClick={() => !confirm.deleted && openConfirmation(confirm._id)}
                component="button"
              >
                {confirm.deleted ? `${locale.Deleted} - ` : ""}
                {moment.utc(confirm.confirmedOn).format("L")}
              </Link>

              <div>
                <UserAvatarChip size="small" userName={confirm.confirmedBy?.Name} />{" "}
                <Button
                  variant={"outlined"}
                  onClick={() =>
                    toggleConfirmation(confirm._id, confirm.deleted, confirm.weeks)
                  }
                >
                  {confirm.deleted ? locale.Restore : locale.Cancel}
                </Button>
              </div>
            </div>

            <DocumentList
              className={classes.documentsWrapper}
              documents={confirm.documents}
              emptyMessage={
                <span style={{ textAlign: "center" }}>
                  Inga boenden bekräftade för tillfället
                </span>
              }
              onDelete={(doc) => removeDoc(doc.FileRef.NodeID)}
            />
          </React.Fragment>
        ))}
      </div>
    </BookingHospitalityDocumentSection>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  item: {
    width: "66%",
    marginTop: 16
  },

  documentsWrapper: {
    padding: 16
  },

  docHeader: {
    padding: "8px 16px",
    background: "antiquewhite",
    borderTop: `1px solid ${theme.separatorColor}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&&.canceled": {
      background: "rgba(255,55,55, 1)",
      color: "#fff",
      opacity: 0.7
    },
    "&& a.canceled": {
      color: "#fff",
      pointerEvents: "none"
    }
  },

  accom: {
    marginTop: 16
  }
}));
